import {HttpResponse} from '@angular/common/http';
import {
    AuthSessionApiService,
    BiometryVendor,
    CheckLivenessResponse,
    CheckLivenessStatus,
    ZoomCheckLivenessRequest,
} from '@npck/auth-frontend-api';
import {isDeviceInPortraitMode, REQUEST_ID_HEADER, rxSpinner, SentryService} from '@npck/libs/commons';
import {Subject} from 'rxjs';
import {
    FaceTecFaceScanProcessor,
    FaceTecFaceScanResultCallback,
    FaceTecSessionResult,
    FaceTecSessionStatus,
} from '../../../../../assets/facetec/core-sdk/FaceTecSDK.js/FaceTecPublicApi';
import {AuthHolderService} from '../../../auth-holder.service';
import {FaceTecResult, FaceTecZoomState} from '../facetec-customization';
import {LivenessService} from '../../../liveness.service';

export class LivenessCheckProcessor implements FaceTecFaceScanProcessor {
    private latestSessionStatus: FaceTecSessionStatus | null;
    private livenessTimeout = this.livenessService.createTimeoutReporter();

    constructor(
        sessionToken: string,
        private authSessionApiService: AuthSessionApiService,
        private authHolderService: AuthHolderService,
        private sentryService: SentryService,
        private livenessService: LivenessService,
        private faceTecStateSubject: Subject<FaceTecResult>,
        private isMobile: boolean,
    ) {
        this.sentryService.addTag('faceTecProcessorStarted', true);
        this.sentryService.addBreadcrumb({
            message: 'FaceTecProcessor constructor started',
            data: {'faceTecSdkStatus': FaceTecSDK.getStatus()},
        });

        this.latestSessionStatus = null;
        this.authSessionApiService = authSessionApiService;
        this.authHolderService = authHolderService;

        new FaceTecSDK.FaceTecSession(this, sessionToken);

        this.faceTecStateSubject.next({
            status: FaceTecZoomState.STARTED,
            rootStatus: FaceTecZoomState.STARTED,
        });
        this.sentryService.addBreadcrumb({
            message: 'FaceTecProcessor constructor finished',
            data: {'faceTecSdkStatus': FaceTecSDK.getStatus()},
        });
    }

    public processSessionResultWhileFaceTecSDKWaits = (
        sessionResult: FaceTecSessionResult,
        faceScanResultCallback: FaceTecFaceScanResultCallback,
    ): void => {
        this.livenessTimeout.cancelTimeout();
        this.sentryService.addTag('processSessionResultWhileFaceTecSDKWaits', true);
        this.sentryService.addBreadcrumb({
            message: 'processSessionResultWhileFaceTecSDKWaits called',
            data: {'sessionStatus': sessionResult.status},
        });

        const isCameraNotEnabled = sessionResult.status === FaceTecSDK.FaceTecSessionStatus.CameraNotEnabled;
        // When a device rotates in landscape mode while the SDK initializes FaceTec returns the CameraNotEnabled status.
        // This is a workaround for checking the device orientation when the CameraNotEnabled status is received
        // and setting the right status in sessionResult to handle this case correctly.
        this.latestSessionStatus =
            this.isMobile && !isDeviceInPortraitMode() && isCameraNotEnabled
                ? FaceTecSDK.FaceTecSessionStatus.LandscapeModeNotAllowed
                : sessionResult.status;

        const status = FaceTecSDK.FaceTecSessionStatus[this.latestSessionStatus];

        if (sessionResult.status !== FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully) {
            console.log('Session was not completed successfully, cancelling.  Session Status: ' + status);
            faceScanResultCallback.cancel();
        } else {
            const request: ZoomCheckLivenessRequest = {
                vendor: BiometryVendor.ZOOM,
                faceScan: sessionResult.faceScan,
                auditTrailHighQualityImageBase64List: sessionResult.auditTrail,
                auditTrailLowQualityImageBase64List: sessionResult.lowQualityAuditTrail,
                sessionId: sessionResult.sessionId,
                installationId: this.getInstallationId(sessionResult.sessionId),
            };
            this.authSessionApiService.checkLiveness(request, 'response')
                .pipe(rxSpinner('livenessStarted'))
                .subscribe({
                    next: response => this.handleResponse(response, faceScanResultCallback),
                    error: () => {
                        this.latestSessionStatus = null;
                        faceScanResultCallback.cancel();
                        this.faceTecStateSubject.next({
                            status: FaceTecZoomState.UNKNOWN,
                            rootStatus: FaceTecSDK.FaceTecSDKStatus[FaceTecSDK.getStatus()],
                            errorSource: 'zoomCheckLivenessRequest error',
                        });
                    },
                });
        }

    };


    public onFaceTecSDKCompletelyDone = (): void => {
        this.livenessTimeout.cancelTimeout();
        this.sentryService.addTag('onFaceTecSDKCompletelyDone', true);
        this.sentryService.addBreadcrumb({
            message: 'FaceTecProcessor onFaceTecSDKCompletelyDone',
            data: {'faceTecSdkStatus': FaceTecSDK.getStatus(), 'latestSessionStatus': this.latestSessionStatus},
        });
        if (this.latestSessionStatus === FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully) {
            this.faceTecStateSubject.next({
                status: FaceTecZoomState.CLOSED,
                rootStatus: FaceTecSDK.FaceTecSessionStatus[this.latestSessionStatus],
            });
        }
        this.latestSessionStatus && this.checkFinalSdkStatus();
    };

    private getInstallationId(sessionId: string): string {
        return FaceTecSDK.createFaceTecAPIUserAgentString?.(sessionId)?.split('|')?.[5];
    }

    private handleResponse(
        response: HttpResponse<CheckLivenessResponse>,
        faceScanResultCallback: FaceTecFaceScanResultCallback,
    ): void {
        switch (response.body.status) {
            case CheckLivenessStatus.SUCCEED:
                faceScanResultCallback.succeed();
                this.faceTecStateSubject.next({
                    status: FaceTecZoomState.SUCCEED,
                    requestId: response.headers.get(REQUEST_ID_HEADER),
                    rootStatus: response.body.status,
                });
                break;
            case CheckLivenessStatus.FAILED_LIVENESS:
                if (
                    !this.authHolderService.livenessQrId &&
                    (this.authHolderService.authSessionData.showAlternativeLivenessWays || !this.isMobile) &&
                    response.body.failedAttemptCount === 2
                ) {
                    faceScanResultCallback.cancel();
                    this.faceTecStateSubject.next({
                        status: FaceTecZoomState.LIVENESS_FAILED,
                        requestId: response.headers.get(REQUEST_ID_HEADER),
                        rootStatus: response.body.status,
                    });
                } else {
                    faceScanResultCallback.retry();
                }
                break;
            case CheckLivenessStatus.FAILED_FACE_NOT_MATCHED:
                if (response.body.failedAttemptCount === 2) {
                    faceScanResultCallback.cancel();
                    this.faceTecStateSubject.next({
                        status: FaceTecZoomState.FACE_NOT_MATCHED,
                        requestId: response.headers.get(REQUEST_ID_HEADER),
                        rootStatus: response.body.status,
                    });
                } else {
                    faceScanResultCallback.retry();
                }
                break;
            case CheckLivenessStatus.LIMIT_PER_SESSION_EXCEEDED:
                faceScanResultCallback.cancel();
                this.faceTecStateSubject.next({
                    status: FaceTecZoomState.LIMIT_PER_SESSION_EXCEEDED,
                    requestId: response.headers.get(REQUEST_ID_HEADER),
                    rootStatus: response.body.status,
                });
                break;
        }
    }


    private checkFinalSdkStatus(): void {
        this.sentryService.addBreadcrumb({
            message: 'FaceTecProcessor checkStatus',
            data: {'faceTecSdkStatus': FaceTecSDK.getStatus(), 'latestSessionStatus': this.latestSessionStatus},
        });
        switch (this.latestSessionStatus) {
            case FaceTecSDK.FaceTecSessionStatus.UserCancelled:
            case FaceTecSDK.FaceTecSessionStatus.UserCancelledFromNewUserGuidance:
            case FaceTecSDK.FaceTecSessionStatus.UserCancelledFromRetryGuidance:
            case FaceTecSDK.FaceTecSessionStatus.UserCancelledWhenAttemptingToGetCameraPermissions:
                this.faceTecStateSubject.next({
                    status: FaceTecZoomState.CANCELLED_BY_USER,
                    rootStatus: FaceTecSDK.FaceTecSessionStatus[this.latestSessionStatus],
                });
                break;
            case FaceTecSDK.FaceTecSessionStatus.ContextSwitch:
            case FaceTecSDK.FaceTecSessionStatus.Timeout:
                this.faceTecStateSubject.next({
                    status: FaceTecZoomState.CANCELLED_BY_FACE_TEC,
                    rootStatus: FaceTecSDK.FaceTecSessionStatus[this.latestSessionStatus],
                });
                break;
            case FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully:
            case FaceTecSDK.FaceTecSessionStatus.DocumentNotReady:
            case FaceTecSDK.FaceTecSessionStatus.SessionInProgress:
                break;
            case FaceTecSDK.FaceTecSessionStatus.LandscapeModeNotAllowed:
            case FaceTecSDK.FaceTecSessionStatus.OrientationChangeDuringSession:
                this.faceTecStateSubject.next({
                    status: FaceTecZoomState.ORIENTATION_ERROR,
                    rootStatus: FaceTecSDK.FaceTecSessionStatus[this.latestSessionStatus]
                });
                break;
            case FaceTecSDK.FaceTecSessionStatus.LockedOut:
                break;
            case FaceTecSDK.FaceTecSessionStatus.CameraNotEnabled:
            case FaceTecSDK.FaceTecSessionStatus.CameraNotRunning:
                this.faceTecStateSubject.next({
                    status: FaceTecZoomState.CAMERA_NOT_FOUND,
                    rootStatus: FaceTecSDK.FaceTecSessionStatus[this.latestSessionStatus],
                });
                break;
            default:
                const sessionStatusDescription = FaceTecSDK.getFriendlyDescriptionForFaceTecSessionStatus(this.latestSessionStatus);
                throw Error(`FaceTec Session failed: status:${this.latestSessionStatus}: ${sessionStatusDescription}`);
        }
    }
}
